import { useState, useCallback } from 'react';
import apiClient from '@/lib/apiClient';
import type { Trip } from '@/types';

/**
 * useTrips — 调用后端 API 获取行程数据（替代 Supabase 直连）
 */
export const useTrips = () => {
  const [trips, setTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTrips = useCallback(async (params: Record<string, unknown> = {}) => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.get('/trips', { params }) as { items?: Trip[] } | Trip[];
      const items = (data as { items?: Trip[] })?.items || data as Trip[];
      setTrips(items);
      return data;
    } catch (err: unknown) {
      const e = err as Error;
      setError(e.message || '获取行程失败');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteTrip = useCallback(async (tripId: string) => {
    await apiClient.delete(`/trips/${tripId}`);
    setTrips((prev) => prev.filter((t) => t.id !== tripId));
  }, []);

  const createTrip = useCallback(async (tripData: Partial<Trip>) => {
    const trip = await apiClient.post('/trips', tripData) as Trip;
    setTrips((prev) => [trip, ...prev]);
    return trip;
  }, []);

  const updateTrip = useCallback(async (tripId: string, updates: Partial<Trip>) => {
    const updated = await apiClient.put(`/trips/${tripId}`, updates) as Trip;
    setTrips((prev) => prev.map((t) => (t.id === tripId ? updated : t)));
    return updated;
  }, []);

  return { trips, loading, error, fetchTrips, createTrip, updateTrip, deleteTrip, setTrips };
};

export default useTrips;
